import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { NumericFormat as NumberFormat } from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import AnimatedCol from "../../../components/AnimatedCol";
import ConvertToString, { getClassName } from "../../../components/Converter";
import CheckboxWithText from "../../../components/Inputs/CheckboxWithText";
import KiloNewton from "../../../components/Math/Units/Metric/KiloNewton";
import Metre from "../../../components/Math/Units/Metric/Metre";
import AlertBox from "../../../components/Messages";
import { NumberFormatText } from "../../../components/NumberFormatText";
import RenderFor, { BM_TYP } from "../../../components/RenderFor";
import { RowBody } from "../../../components/RowValue/RowBody";
import ToolTip, { IconType } from "../../../components/ToolTip";
import Headline from "../../../components/UI/Headline";
import MainArea from "../../../components/UI/MainArea";
import { ResultSpan } from "../../../components/styledComponents/Result";
import { DRUCKPFAHL, ZUGPFAHL } from "../../../constants.js";
import { decimalSeparator, thousandSeparator } from "../../../i18n";
import {
  selectBerechnet,
  selectIsLreqFactorValid,
  selectIsUtilizationFactorValid,
  selectLongestPfahlValue,
  selectLongestPfahlValuePlusExtension,
  selectMinimalePfahllaenge,
  selectPfahllaengeGewaehltInBoden,
  selectPileCapExtension,
  selectShowError_Druckpfahlgruppe,
  selectShowError_Zugpfahlgruppe,
  selectZgruppeLreqIsValid,
  setPfahllaengeGewaehltInBoden,
  setPfahllaengeGewaehltInBodenIsCustom,
  setPileCapExtension,
} from "../../../store/slices/aeussereSlice";
import { selectBelastungsartUndRichtung } from "../../../store/slices/belastungsartUndRichtungSlice";
import {
  BemessungenMikropfaehle,
  selectBemessungenMikropfaehle,
} from "../../../store/slices/bemessungenMikropfaehleSlice";
import {
  selectFrontendZustand,
  setKompressionspfahlgruppeCheckbox,
  setUEberpruefungVonPfahlgruppenCheckbox,
  setUEbertragungslaengeCheckbox,
} from "../../../store/slices/frontendZustandSlice.js";
import DesignLoad from "../Innere/DesignLoad";
import DrillBitDiameter from "../Innere/DrillBitDiameter";
import LoadType from "../Innere/LoadType";
import SelectedLoadCarryingElement from "../Innere/SelectedLoadCarryingElement";
import { eta_Formula } from "../TeXFormula.js";
import AussereTable from "./AussereTable";
import Druckpfahlgruppe from "./Druckpfahlgruppe";
import { Modelfactor } from "./Modelfactor";
import { PfahlneigungZurVertikalen } from "./PfahlneigungZurVertikalen";
import { TeilsicherheitsbeiwertAeussere } from "./TeilsicherheitsbeiwertAeussere";
import Transmission from "./Transmission";
import Zugpfahlgruppe from "./Zugpfahlgruppe";

export default function Aussere() {
  const { t } = useTranslation("translation");
  const dispatch = useDispatch();

  // #region Redux Store Variables
  const minimalePfahllaenge = useSelector(selectMinimalePfahllaenge);
  const belastungsartUndRichtung = useSelector(selectBelastungsartUndRichtung);
  const pfahllaengeGewaehltInBoden = useSelector(selectPfahllaengeGewaehltInBoden);
  const {
    uebertragungslaengeCheckbox,
    ueberpruefungVonPfahlgruppenCheckbox,
    kompressionspfahlgruppenCheckbox,
  } = useSelector(selectFrontendZustand);
  const pileCapExtension = useSelector(selectPileCapExtension);
  const longestPfahlValue = useSelector(selectLongestPfahlValue);
  const longestPfahlValuePlusExtension = useSelector(selectLongestPfahlValuePlusExtension);
  const berechnet = useSelector(selectBerechnet);
  const isUtilizationFactorValid = useSelector(selectIsUtilizationFactorValid);
  const frontendTest = useSelector(selectFrontendZustand);
  const isLreqValid_DGruppe = useSelector(selectIsLreqFactorValid);
  const isLreqValid_ZGruppe = useSelector(selectZgruppeLreqIsValid);
  const bemessungenMikropfaehle = useSelector(selectBemessungenMikropfaehle);

  const showGroupError_DGruppe = useSelector(selectShowError_Druckpfahlgruppe);
  const showGroupError_ZGruppe = useSelector(selectShowError_Zugpfahlgruppe);

  let groupCheckboxOn =
    belastungsartUndRichtung === DRUCKPFAHL
      ? frontendTest.kompressionspfahlgruppenCheckbox
      : frontendTest.ueberpruefungVonPfahlgruppenCheckbox;

  const showGroupError = groupCheckboxOn
    ? belastungsartUndRichtung === DRUCKPFAHL
      ? showGroupError_DGruppe
      : showGroupError_ZGruppe
    : false;

  const lReqGruppeValid = groupCheckboxOn
    ? belastungsartUndRichtung === DRUCKPFAHL
      ? isLreqValid_DGruppe
      : isLreqValid_ZGruppe
    : true;

  const isLreqValid = groupCheckboxOn ? lReqGruppeValid : true;
  // #endregion

  const handleTransmission = () =>
    dispatch(setUEbertragungslaengeCheckbox(!uebertragungslaengeCheckbox));

  const handleZGroupe = () =>
    dispatch(setUEberpruefungVonPfahlgruppenCheckbox(!ueberpruefungVonPfahlgruppenCheckbox));

  const handleKompressionPfahlGruppe = () =>
    dispatch(setKompressionspfahlgruppeCheckbox(!kompressionspfahlgruppenCheckbox));

  return (
    <MainArea>
      {/* Headline & Compression/Tension Selection */}
      <Headline isPrimary text={t("aeussre.outerLoadCarryingCapacity")} />

      {/* Data */}
      <div>
        <div className="mt-5">
          <Row>
            <Headline text={t("inner.fdCalculation.headline")} underlined={true} />
          </Row>
          <Row>
            <AnimatedCol>
              <DesignLoad readOnly={true} />
            </AnimatedCol>
          </Row>
          <Row>
            <AnimatedCol>
              <SelectedLoadCarryingElement readOnly={true} />
            </AnimatedCol>
          </Row>
          <Row>
            <AnimatedCol>
              <LoadType readOnly={true} />
            </AnimatedCol>
          </Row>
          <Row>
            <AnimatedCol>
              <DrillBitDiameter readOnly={true} />
            </AnimatedCol>
          </Row>
          <Row>
            <AnimatedCol>
              <PfahlneigungZurVertikalen />
            </AnimatedCol>
          </Row>
          <Row>
            <AnimatedCol>
              <TeilsicherheitsbeiwertAeussere />
            </AnimatedCol>
          </Row>

          {belastungsartUndRichtung === ZUGPFAHL ? (
            <RenderFor types={[BM_TYP.GERMANY]}>
              <Row>
                <AnimatedCol>
                  <Modelfactor />
                </AnimatedCol>
              </Row>
            </RenderFor>
          ) : null}
        </div>

        <div className="mt-5">
          <Row>
            <Headline text={t("aeussre.soilLayers")} />
            <AnimatedCol>
              <AussereTable />
            </AnimatedCol>
          </Row>
        </div>
        <div className="mt-5">
          <Row>
            <AnimatedCol>
              <RowBody
                titleValue={<p>{t("aeussre.minPileLength")}</p>}
                indicesValue={
                  <span>
                    L<sub>req</sub>
                  </span>
                }
                inputValue={
                  <span className="input">
                    <NumberFormatText value={minimalePfahllaenge} decimalScale={2} />
                  </span>
                }
                unitValue={<Metre />}
              />
            </AnimatedCol>
          </Row>
          <Row>
            <AnimatedCol>
              <RowBody
                titleValue={<p>{t("aeussre.pileLengthChoosen")}</p>}
                indicesValue={
                  <span>
                    L<sub>nom</sub>
                  </span>
                }
                inputValue={
                  <NumberFormat
                    className={
                      showGroupError && !isLreqValid
                        ? "change-input-error"
                        : !isUtilizationFactorValid
                          ? "change-input-error"
                          : getClassName(pfahllaengeGewaehltInBoden)
                    }
                    value={pfahllaengeGewaehltInBoden}
                    decimalSeparator={decimalSeparator()}
                    thousandSeparator={thousandSeparator()}
                    displayType="input"
                    type="text"
                    decimalScale={2}
                    //fixedDecimalScale={true}
                    onValueChange={(value, sourceInfo) => {
                      if (value.floatValue) {
                        if (sourceInfo.source === "prop") {
                          return;
                        }
                        if (sourceInfo?.source === "event" ?? false) {
                          dispatch(setPfahllaengeGewaehltInBoden(value.floatValue));
                          dispatch(setPfahllaengeGewaehltInBodenIsCustom(true));
                        }
                      } else {
                        if (sourceInfo?.source === "event" ?? false) {
                          dispatch(setPfahllaengeGewaehltInBoden(0));
                          dispatch(setPfahllaengeGewaehltInBodenIsCustom(false));
                        }
                      }
                    }}
                    allowNegative={true}
                  />
                }
                unitValue={<Metre />}
              />
            </AnimatedCol>
          </Row>
          <Row>
            <Col md={8}>
              <AlertBox
                show={
                  Number(Number(minimalePfahllaenge).toFixed(2)) >
                  Number(Number(pfahllaengeGewaehltInBoden).toFixed(2))
                }
                message={t("aeussre.error1")}
              />
            </Col>
          </Row>
          <Row>
            <AnimatedCol>
              <RowBody
                titleValue={<p>{t("aeussre.designedPileResistance")}</p>}
                indicesValue={
                  <span>
                    R<sub>d,nom</sub>
                  </span>
                }
                inputValue={
                  <span className="input">
                    <NumberFormatText value={berechnet.designedPileResistance} decimalScale={0} />
                  </span>
                }
                unitValue={<KiloNewton />}
              />
            </AnimatedCol>
          </Row>
          <Row>
            <AnimatedCol>
              <RowBody
                titleValue={<p>{t("aeussre.utilizationFactor")}</p>}
                indicesValue={<span>&eta;</span>}
                inputValue={
                  <ResultSpan valid={isUtilizationFactorValid}>
                    {ConvertToString(berechnet.utilizationFactor, 2)}
                  </ResultSpan>
                }
                formulaValue={
                  <ToolTip id={"eta_Formula"} type={IconType.FORMULA} formula={eta_Formula} />
                }
              />
            </AnimatedCol>
          </Row>
        </div>
        <div className="mt-5">
          <Row>
            <AnimatedCol>
              <h3>{t("aeussre.determiningTotalLength")}</h3>
            </AnimatedCol>
          </Row>
          <Row>
            <AnimatedCol>
              <RowBody
                titleValue={<p>{t("aeussre.pileCapExtension")}</p>}
                indicesValue={
                  <span>
                    L<sub>0</sub>
                  </span>
                }
                inputValue={
                  <NumberFormat
                    value={pileCapExtension}
                    className={getClassName(pileCapExtension)}
                    decimalSeparator={decimalSeparator()}
                    thousandSeparator={thousandSeparator()}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    displayType="input"
                    type="text"
                    onValueChange={(value) => dispatch(setPileCapExtension(value.floatValue))}
                  />
                }
                unitValue={<Metre />}
              />
            </AnimatedCol>
          </Row>
          <Row>
            <AnimatedCol>
              <RowBody
                titleValue={<p>{t("aeussre.pileLength")}</p>}
                indicesValue={
                  <span>
                    L<sub>nom</sub>
                  </span>
                }
                inputValue={
                  <span className="input">
                    <NumberFormatText value={longestPfahlValue} decimalScale={2} />
                  </span>
                }
                unitValue={<Metre />}
              />
            </AnimatedCol>
          </Row>
          <Row>
            <AnimatedCol>
              <RowBody
                titleValue={<p>{t("aeussre.totalPileLength")}</p>}
                indicesValue={
                  <span>
                    L<sub>tot</sub>
                  </span>
                }
                inputValue={
                  <span className="input">
                    <NumberFormatText value={longestPfahlValuePlusExtension} decimalScale={2} />
                  </span>
                }
                unitValue={<Metre />}
              />
            </AnimatedCol>
          </Row>
        </div>
      </div>

      {/*Selection for additional Content: Pile Group & Transmission */}
      <div>
        {belastungsartUndRichtung === DRUCKPFAHL ? (
          <>
            <Row>
              <Col>
                <CheckboxWithText
                  testId="checkbox-pile-group"
                  checked={kompressionspfahlgruppenCheckbox}
                  onChange={handleKompressionPfahlGruppe}
                  text={t("aeussre.pileGroupCheckboxText")}
                  subtext={t("aeussre.pileGroupCheckboxText_sub")}
                />
              </Col>
            </Row>
            <Row>
              <Col>{kompressionspfahlgruppenCheckbox === true ? <Druckpfahlgruppe /> : null}</Col>
            </Row>
          </>
        ) : null}

        {belastungsartUndRichtung === ZUGPFAHL ? (
          <>
            <Row>
              <Col>
                <CheckboxWithText
                  testId="checkbox-tension-group"
                  checked={ueberpruefungVonPfahlgruppenCheckbox}
                  onChange={handleZGroupe}
                  text={t("aeussre.verificationCheckboxText")}
                  subtext={t("aeussre.verificationCheckboxText_sub")}
                />
              </Col>
            </Row>
            <Row>
              <Col>{ueberpruefungVonPfahlgruppenCheckbox === true ? <Zugpfahlgruppe /> : null}</Col>
            </Row>
          </>
        ) : null}

        <Row>
          <Col>
            <CheckboxWithText
              testId="checkbox-transmission"
              checked={uebertragungslaengeCheckbox}
              onChange={handleTransmission}
              text={t("aeussre.transmissionCheckboxText")}
              subtext={
                bemessungenMikropfaehle === BemessungenMikropfaehle.EUROCODE_7
                  ? t("aeussre.transmissionCheckboxText_sub_ec7")
                  : t("aeussre.transmissionCheckboxText_sub")
              }
            />
          </Col>
        </Row>
        <Row>
          <Col>{uebertragungslaengeCheckbox === true ? <Transmission /> : null}</Col>
        </Row>
      </div>
    </MainArea>
  );
}
